// Fixed brand → color mapping (Hygenix product catalogue).
//   Dew Drop  → orange  #E67E22
//   Hug Me    → purple  #8E44AD
//   Total Care→ blue    #2980B9
//
// Each canonical brand has a fixed list of products (variants). Every raw brand
// string entered on an order is matched (case & space insensitive) to exactly
// one product within one canonical brand. Anything that doesn't match one of
// the three brand families is silently excluded from the dashboard "Orders by
// Brand" chart — so the pie always shows just the 3 real brands.

export const CANONICAL_BRANDS = [
  { key: "Dew Drop",   color: "#E67E22" },
  { key: "Hug Me",     color: "#8E44AD" },
  { key: "Total Care", color: "#2980B9" },
];

// Product rules are evaluated top-to-bottom, first match wins.
// The `match` list is an array of normalized (lowercase, no-space) substrings.
// Order matters: more-specific product rules come before broader family fallbacks.
export const PRODUCT_RULES = [
  // Dew Drop family
  { canonical: "Dew Drop",   product: "Dew Drop Diaper",    match: ["dewdropdiaper"] },
  { canonical: "Dew Drop",   product: "Dew Drop Pant",      match: ["dewdroppant"] },
  { canonical: "Dew Drop",   product: "Dew Drop",           match: ["dewdrop"] }, // brand fallback

  // Hug Me family — Pant Lite comes BEFORE Pant to avoid mis-matching.
  { canonical: "Hug Me",     product: "HMBP",               match: ["hmbp"] },
  { canonical: "Hug Me",     product: "Hug Me Pant Lite",   match: ["hugmepantlite", "hugmelite"] },
  { canonical: "Hug Me",     product: "Hug Me Diaper",      match: ["hugmediaper"] },
  { canonical: "Hug Me",     product: "Hug Me Pant",        match: ["hugmepant"] },
  { canonical: "Hug Me",     product: "Hug Me",             match: ["hugme"] }, // brand fallback

  // Total Care family
  { canonical: "Total Care", product: "Total Care Diaper",  match: ["totalcarediaper"] },
  { canonical: "Total Care", product: "Total Care Pant",    match: ["totalcarepant"] },
  { canonical: "Total Care", product: "Total Care",         match: ["totalcare"] }, // brand fallback
];

function normalizeKey(name) {
  return String(name || "").toLowerCase().replace(/\s+/g, "").replace(/_/g, "");
}

// Returns { canonical, product } or null if the raw name doesn't belong to any
// of the three canonical brand families.
export function classifyBrand(name) {
  const k = normalizeKey(name);
  if (!k) return null;
  for (const rule of PRODUCT_RULES) {
    if (rule.match.some((m) => k.includes(m))) {
      return { canonical: rule.canonical, product: rule.product };
    }
  }
  return null;
}

export function canonicalBrand(name) {
  return classifyBrand(name)?.canonical || null;
}

export function canonicalColor(key) {
  return CANONICAL_BRANDS.find((b) => b.key === key)?.color || "#5C6B61";
}

// Loose colour lookup for a raw brand (used inline in tables, badges, etc.)
export function brandColor(name, fallback = "#5C6B61") {
  return canonicalColor(canonicalBrand(name)) || fallback;
}

// Aggregate raw {brand, quantity} rows into the 3 canonical brand buckets.
// Non-matching brands are dropped entirely — the main pie shows ONLY the
// three real brands the business sells.
export function aggregateByCanonical(rows) {
  const totals = { "Dew Drop": 0, "Hug Me": 0, "Total Care": 0 };
  for (const r of rows || []) {
    const c = canonicalBrand(r.brand);
    if (c && totals.hasOwnProperty(c)) totals[c] += Number(r.quantity || 0);
  }
  return CANONICAL_BRANDS
    .filter((b) => totals[b.key] > 0)
    .map((b) => ({ brand: b.key, quantity: totals[b.key], color: b.color }));
}

// Given the raw {brand, quantity} rows, returns a per-canonical map of
// product-level totals: { "Dew Drop": { "Dew Drop Diaper": 25, "Dew Drop Pant": 10 }, ... }
export function productBreakdownByCanonical(rows) {
  const out = { "Dew Drop": {}, "Hug Me": {}, "Total Care": {} };
  for (const r of rows || []) {
    const c = classifyBrand(r.brand);
    if (!c) continue;
    const bucket = out[c.canonical];
    bucket[c.product] = (bucket[c.product] || 0) + Number(r.quantity || 0);
  }
  return out;
}
